import { Head, useForm } from '@inertiajs/react';
import { FormEvent } from 'react';

interface Props {
    suggestedUsername: string;
}

/**
 * Username selection page - shown after Discord auth for new users.
 * Users must choose a unique username before accessing the app.
 */
export default function Username({ suggestedUsername }: Props) {
    const { data, setData, post, processing, errors } = useForm({
        username: suggestedUsername,
    });

    const handleSubmit = (e: FormEvent) => {
        e.preventDefault();
        post('/onboarding/username');
    };

    return (
        <>
            <Head title="Choose Your Username" />

            <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
                <div className="max-w-md w-full">
                    <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 border border-white/20">
                        <div className="text-center mb-8">
                            <h1 className="text-3xl font-bold text-white mb-2">
                                Choose Your Username
                            </h1>
                            <p className="text-gray-300">
                                This is how other players will see you in Sellune.
                            </p>
                        </div>

                        <form onSubmit={handleSubmit} className="space-y-6">
                            <div>
                                <label
                                    htmlFor="username"
                                    className="block text-sm font-medium text-gray-300 mb-2"
                                >
                                    Username
                                </label>
                                <input
                                    type="text"
                                    id="username"
                                    value={data.username}
                                    onChange={(e) => setData('username', e.target.value)}
                                    className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                    placeholder="Enter your username"
                                    minLength={3}
                                    maxLength={30}
                                    pattern="[a-zA-Z0-9_-]+"
                                    required
                                    autoFocus
                                />
                                {errors.username && (
                                    <p className="mt-2 text-sm text-red-400">{errors.username}</p>
                                )}
                                <p className="mt-2 text-xs text-gray-300">
                                    3-30 characters. Letters, numbers, underscores, and hyphens only.
                                </p>
                            </div>

                            <button
                                type="submit"
                                disabled={processing}
                                className="w-full bg-purple-600 hover:bg-purple-700 disabled:bg-purple-600/50 text-white font-semibold py-3 px-6 rounded-lg transition-colors"
                            >
                                {processing ? 'Saving...' : 'Continue to Sellune'}
                            </button>
                        </form>
                    </div>
                </div>
            </div>
        </>
    );
}

